import { Controller } from "@hotwired/stimulus"
import { show_error, show_success, htmlEscape } from "lib/helpers"
import { createTurboDataTable, destroyTurboDataTable } from "lib/datatables_turbo"

// chat--dialog-edit on `app/views/chat/dialogs/edit.haml` (#workspace root).
export default class extends Controller {
  static values = {
    dialogMembersPath: String,
    dialogType: Number,
    findUsersPath: String,
    addOccupantsPath: String,
    removeOccupantsPath: String,
    addAdminsPath: String,
    dialogsPath: String,
    updateRolePath: String,
    ownerId: String,
    usersServiceRoot: String,
    isReader: Boolean,
  }

  static targets = [
    "searchInput",
    "membersFilter",
    "membersTable",
    "removeMembersBtn",
    "addMembersBtn",
    "form",
    "submitBtn",
    "cancelBtn",
    "confirmRemoveModal",
    "confirmRemoveBtn",
    "confirmRoleModal",
    "confirmRoleBtn",
    "confirmRoleText",
    "ownerModal",
  ]

  connect() {
    this.selectedMembers = []
    this.typeFilter = "all"
    this.#initDropdown()
    this.#initDataTable()
  }

  disconnect() {
    this.#closeCellDropdown()
    if (this.dropdown) {
      document.removeEventListener("click", this.boundDropdownDocClick)
      this.dropdown = null
    }
    if (this.dataTable) {
      destroyTurboDataTable(this.dataTable)
      this.dataTable = null
    }
  }

  submitForm(event) {
    event.preventDefault()
    const form = this.formTarget
    const formData = new FormData(form)

    fetch(form.action, {
      method: "PUT",
      headers: {
        "X-Requested-With": "XMLHttpRequest",
        "X-CSRF-Token": this.#csrfToken,
      },
      body: formData,
      credentials: "same-origin",
    })
      .then((response) => {
        if (!response.ok) throw new Error(`HTTP ${response.status}: ${response.statusText}`)
        return response.json()
      })
      .then((response) => {
        if (response.success) show_success("Dialog updated successfully")
      })
      .catch((error) => {
        window.scrollTo(0, 0)
        show_error(error.message || "An error occurred while updating the dialog")
      })
  }

  cancelForm(event) {
    event.preventDefault()
    window.location.href = this.dialogsPathValue
  }

  searchMembers(event) {
    if (this.dataTable) {
      this.dataTable.search(event.target.value).draw()
    }
  }

  openAddModal(event) {
    event.preventDefault()
    if (this.dialogTypeValue === 1) {
      const modal = document.getElementById("add-admins-modal")
      if (modal) $(modal).modal("show")
    } else if (this.dialogTypeValue === 2) {
      const modal = document.getElementById("add-members-modal")
      if (modal) $(modal).modal("show")
    }
  }

  removeSelectedMembers(event) {
    event.preventDefault()
    if (this.selectedMembers.length === 0) return

    if (this.selectedMembers.some((m) => m.id == this.ownerIdValue)) {
      if (this.hasOwnerModalTarget) {
        new bootstrap.Modal(this.ownerModalTarget).show()
      }
      return
    }

    if (this.hasConfirmRemoveModalTarget) {
      this.pendingRemoveModal = new bootstrap.Modal(this.confirmRemoveModalTarget)
      this.pendingRemoveModal.show()
    }
  }

  confirmRemoveMembers() {
    if (this.pendingRemoveModal) this.pendingRemoveModal.hide()
    this.#removeMembers(this.selectedMembers)
  }

  requestRoleChange({ params: { userId, userName, newRole } }) {
    if (this.hasConfirmRoleModalTarget) {
      this.pendingRoleUserId = userId
      this.pendingRoleUserName = userName
      this.pendingRoleNewRole = newRole

      if (this.hasConfirmRoleTextTarget) {
        const roleLabel = newRole === "occupant" ? "Occupant" : "Admin"
        this.confirmRoleTextTarget.textContent =
          `Are you sure you want to change member role for ${userName} to ${roleLabel}?`
      }
      this.roleModal = new bootstrap.Modal(this.confirmRoleModalTarget)
      this.roleModal.show()
    }
  }

  confirmRoleChange() {
    if (this.roleModal) this.roleModal.hide()
    this.#sendUpdateMemberRoleRequest(this.pendingRoleUserId, this.pendingRoleUserName, this.pendingRoleNewRole)
  }

  membersReload() {
    if (this.dataTable) this.dataTable.ajax.reload()
  }

  // --- private ---

  #initDropdown() {
    if (!this.hasMembersFilterTarget) return

    this.dropdownItems = [
      { name: "Everyone", value: "all" },
      { name: "Occupants", value: "occupants" },
      { name: "Admins", value: "admins" },
      { name: "Owner", value: "owner" },
    ]
    this.dropdownSelected = this.dropdownItems[0]
    this.dropdownOpen = false
    this.boundDropdownDocClick = this.#handleDropdownDocClick.bind(this)
    document.addEventListener("click", this.boundDropdownDocClick)
    this.#renderDropdown()
  }

  #renderDropdown() {
    const container = this.membersFilterTarget
    container.innerHTML = `
      <div class="dropdown-selected" data-action="click->chat--dialog-edit#toggleDropdown">
        <span class="dropdown-text">${this.dropdownSelected.name}</span>
        <span class="dropdown-arrow">
          <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
            <g clip-path="url(#clip0_5961_53830)">
              <path d="M8 10L12 14L16 10H8Z" fill="#6C7A92"/>
            </g>
            <defs>
              <clipPath id="clip0_5961_53830">
                <rect width="24" height="24" fill="white"/>
              </clipPath>
            </defs>
          </svg>
        </span>
      </div>
      <ul class="dropdown-options ${this.dropdownOpen ? "" : "hidden"}">
        ${this.dropdownItems
          .map(
            (item) =>
              `<li data-value="${item.value}" data-action="click->chat--dialog-edit#selectDropdownItem" style="background-color: ${this.dropdownSelected.value === item.value ? "#D9E3F7" : "transparent"};">${item.name}</li>`
          )
          .join("")}
      </ul>
    `
  }

  toggleDropdown(event) {
    event.stopPropagation()
    this.dropdownOpen = !this.dropdownOpen
    this.#renderDropdown()
  }

  selectDropdownItem(event) {
    event.stopPropagation()
    const value = event.currentTarget.dataset.value
    this.dropdownSelected = this.dropdownItems.find((i) => i.value === value)
    this.dropdownOpen = false
    this.typeFilter = value
    this.#renderDropdown()
    if (this.dataTable) this.dataTable.ajax.reload()
  }

  #handleDropdownDocClick(e) {
    if (this.hasMembersFilterTarget && !this.membersFilterTarget.contains(e.target) && this.dropdownOpen) {
      this.dropdownOpen = false
      this.#renderDropdown()
    }
  }

  #initDataTable() {
    if (!this.hasMembersTableTarget) return

    const options = this.#getDataTableOptions()
    this.dataTable = createTurboDataTable(this.membersTableTarget, options)
  }

  #getDataTableOptions() {
    switch (this.dialogTypeValue) {
      case 1:
        return this.#publicChatOptions()
      case 2:
        return this.#groupChatOptions()
      case 3:
        return this.#privateChatOptions()
      default:
        return this.#baseOptions()
    }
  }

  #baseOptions() {
    return {
      dom: 'lr<"head-toolbar">tip<"footer-toolbar">',
      pagingType: "full_numbers",
      processing: true,
      serverSide: true,
      scrollX: true,
      ordering: false,
      language: {},
      ajax: {
        url: this.dialogMembersPathValue,
        type: "POST",
        headers: { "X-CSRF-Token": this.#csrfToken },
        data: (d) => {
          d.typeFilter = this.typeFilter || "all"
        },
      },
      initComplete: () => {},
      drawCallback: () => {
        this.#closeCellDropdown()
        const checkAll = document.getElementById("check-all-members")
        if (checkAll) {
          const all = this.membersTableTarget.querySelectorAll('input[type="checkbox"].table-checkbox-members')
          if (all.length === 0) {
            checkAll.checked = false
            return
          }
          checkAll.checked = Array.from(all).every((cb) => cb.checked)
        }
      },
    }
  }

  #groupChatOptions() {
    const options = this.#baseOptions()
    options.footerCallback = () => {
      $("#check-all-members").attr("checked", false)
    }
    options.columnDefs = [
      {
        targets: 0,
        createdCell: (cell, cellData) => {
          const isChecked = this.selectedMembers.some((m) => m.id == cellData)
          const checked = isChecked ? " checked" : ""
          $(cell).html(
            `<input type="checkbox" class="table-checkbox table-checkbox-members" id="check--history${cellData}" name="item_ids[]" value="${cellData}"${checked}/> <label for="check--history${cellData}"><label/>`
          )
          $(cell).find('input[type="checkbox"]').on("change", (e) => this.#onRowCheckboxChange(e))
        },
      },
      {
        targets: 1,
        width: "197px",
        createdCell: (cell, cellData) => {
          $(cell).html(`<a href="${this.usersServiceRootValue}/edit/${htmlEscape(cellData)}">${htmlEscape(cellData)}</a>`)
        },
      },
      {
        targets: 2,
        width: "408px",
        createdCell: (cell, cellData) => {
          const cropped = htmlEscape(cellData).length > 20 ? htmlEscape(cellData).substring(0, 20) + "..." : htmlEscape(cellData)
          $(cell).html(cropped)
        },
      },
      {
        targets: 3,
        width: "179px",
        createdCell: (cell, cellData, rowData) => {
          if ((cellData === "occupant" || cellData === "admin") && !this.isReaderValue) {
            setTimeout(() => {
              this.#renderCellDropdown(cell, cellData, rowData[1], rowData[2])
            }, 0)
          } else if (cellData === "owner") {
            $(cell).html(htmlEscape("Owner"))
          } else {
            $(cell).html(htmlEscape(cellData.charAt(0).toUpperCase() + cellData.slice(1)))
          }
        },
      },
      { targets: [0, 1, 2, 3], sortable: false },
    ]
    return options
  }

  #publicChatOptions() {
    const options = this.#baseOptions()
    options.footerCallback = () => {
      $("#check-all-members").attr("checked", false)
    }
    options.columnDefs = [
      {
        targets: 0,
        createdCell: (cell, cellData) => {
          const isChecked = this.selectedMembers.some((m) => m.id == cellData)
          const checked = isChecked ? " checked" : ""
          $(cell).html(
            `<input type="checkbox" class="table-checkbox table-checkbox-members" id="check--history${cellData}" name="item_ids[]" value="${cellData}"${checked}/> <label for="check--history${cellData}"><label/>`
          )
          $(cell).find('input[type="checkbox"]').on("change", (e) => this.#onRowCheckboxChange(e))
        },
      },
      {
        targets: 1,
        width: "197px",
        createdCell: (cell, cellData) => {
          $(cell).html(`<a href="${this.usersServiceRootValue}/edit/${htmlEscape(cellData)}">${htmlEscape(cellData)}</a>`)
        },
      },
      {
        targets: 2,
        width: "408px",
        createdCell: (cell, cellData) => {
          const cropped = htmlEscape(cellData).length > 20 ? htmlEscape(cellData).substring(0, 20) + "..." : htmlEscape(cellData)
          $(cell).html(cropped)
        },
      },
      {
        targets: 3,
        width: "179px",
        createdCell: (cell, cellData) => {
          $(cell).html(this.#memberTypeToString(cellData))
        },
      },
      { targets: [0, 1, 2, 3], sortable: false },
    ]
    return options
  }

  #privateChatOptions() {
    const options = this.#baseOptions()
    options.columnDefs = [
      {
        targets: 0,
        width: "197px",
        createdCell: (cell, cellData) => {
          $(cell).html(`<a href="${this.usersServiceRootValue}/edit/${htmlEscape(cellData)}">${htmlEscape(cellData)}</a>`)
        },
      },
      {
        targets: 1,
        width: "408px",
        createdCell: (cell, cellData) => {
          const cropped = htmlEscape(cellData).length > 20 ? htmlEscape(cellData).substring(0, 20) + "..." : htmlEscape(cellData)
          $(cell).html(cropped)
        },
      },
      {
        targets: 2,
        width: "179px",
        createdCell: (cell, cellData) => {
          $(cell).html(this.#memberTypeToString(cellData))
        },
      },
      { targets: [0, 1, 2], sortable: false },
    ]
    return options
  }

  checkAllMembers(event) {
    const isChecked = event.target.checked
    this.membersTableTarget.querySelectorAll('input[type="checkbox"].table-checkbox-members').forEach((cb) => {
      cb.checked = isChecked
      const value = cb.value
      if (isChecked && !this.selectedMembers.some((m) => m.id === value)) {
        this.selectedMembers.push({ id: value })
      } else if (!isChecked) {
        this.selectedMembers = this.selectedMembers.filter((m) => m.id !== value)
      }
    })
    this.#updateRemoveButton()
  }

  #onRowCheckboxChange(event) {
    const cb = event.target
    const value = cb.value
    if (cb.checked && !this.selectedMembers.some((m) => m.id === value)) {
      this.selectedMembers.push({ id: value })
    } else if (!cb.checked) {
      this.selectedMembers = this.selectedMembers.filter((m) => m.id !== value)
    }
    const all = this.membersTableTarget.querySelectorAll('input[type="checkbox"].table-checkbox-members')
    const allChecked = all.length > 0 && Array.from(all).every((c) => c.checked)
    const checkAll = document.getElementById("check-all-members")
    if (checkAll) checkAll.checked = allChecked
    this.#updateRemoveButton()
  }

  #updateRemoveButton() {
    if (!this.hasRemoveMembersBtnTarget) return
    const btn = this.removeMembersBtnTarget
    if (this.selectedMembers.length > 0) {
      btn.classList.remove("inactive")
    } else {
      btn.classList.add("inactive")
    }
    btn.textContent = `Remove (${this.selectedMembers.length})`
  }

  #removeMembers(members) {
    const userIds = members.map((u) => u.id)
    fetch(this.removeOccupantsPathValue, {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ occupants_ids: userIds }),
    })
      .then((response) => {
        if (!response.ok) throw new Error("Forbidden")
        return response.json()
      })
      .then((response) => {
        this.#clearSelectedMembers()
        if (response.success) {
          this.dataTable.ajax.reload()
          show_success("Members removed successfully")
        }
      })
      .catch((error) => {
        this.#clearSelectedMembers()
        window.scrollTo(0, 0)
        show_error("An error occurred while removing members: " + (error.message || "Unknown error"))
      })
  }

  #sendUpdateMemberRoleRequest(userId, userName, newRole) {
    fetch(this.updateRolePathValue, {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ user_id: userId, role: newRole }),
    })
      .then((response) => {
        if (!response.ok) throw new Error("Forbidden")
        return response.json()
      })
      .then((response) => {
        if (response.success) {
          this.dataTable.ajax.reload()
        } else {
          show_error(`Failed to update member ${userName}: ${response.error}`)
        }
      })
      .catch((error) => {
        window.scrollTo(0, 0)
        show_error("An error occurred while updating member type: " + (error.message || "Unknown error"))
      })
  }

  #clearSelectedMembers() {
    this.selectedMembers = []
    this.membersTableTarget.querySelectorAll('input[type="checkbox"].table-checkbox-members').forEach((cb) => {
      cb.checked = false
    })
    const checkAll = document.getElementById("check-all-members")
    if (checkAll) checkAll.checked = false
    this.#updateRemoveButton()
  }

  #renderCellDropdown(cell, currentValue, userId, userName) {
    const options = [
      { value: "occupant", label: "Occupant" },
      { value: "admin", label: "Admin" },
    ]
    const selected = options.find((o) => o.value === currentValue) || options[0]

    cell.innerHTML = `
      <div class="cell-dropdown">
        <div class="cell-dropdown-selected">
          <span class="cell-dropdown-text">${selected.label}</span>
          <span class="cell-dropdown-arrow">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
              <g clip-path="url(#clip0_5961_53830)"><path d="M8 10L12 14L16 10H8Z" fill="#6C7A92"/></g>
              <defs><clipPath id="clip0_5961_53830"><rect width="24" height="24" fill="white"/></clipPath></defs>
            </svg>
          </span>
        </div>
        <ul class="cell-dropdown-options hidden">
          ${options.map((o) => `<li data-value="${o.value}" style="background-color: ${currentValue === o.value ? "#D9E3F7" : "transparent"};">${o.label}</li>`).join("")}
        </ul>
      </div>
    `

    const dropdownEl = cell.querySelector(".cell-dropdown")
    const selectedEl = cell.querySelector(".cell-dropdown-selected")
    const optionsEl = cell.querySelector(".cell-dropdown-options")

    selectedEl.addEventListener("click", (e) => {
      e.stopPropagation()
      const isOpen = this.openCellDropdown?.optionsEl === optionsEl
      this.#closeCellDropdown()
      if (!isOpen) this.#openCellDropdown(dropdownEl, selectedEl, optionsEl)
    })

    optionsEl.querySelectorAll("li").forEach((li) => {
      li.addEventListener("click", (e) => {
        e.stopPropagation()
        this.#closeCellDropdown()
        const newRole = li.dataset.value
        if (newRole !== currentValue) {
          this.requestRoleChange({ params: { userId, userName, newRole } })
        }
      })
    })
  }

  #openCellDropdown(dropdownEl, triggerEl, optionsEl) {
    this.#closeCellDropdown()

    document.body.appendChild(optionsEl)
    optionsEl.classList.remove("hidden")

    const menuWidth = 226
    const menuHeight = optionsEl.offsetHeight
    const rect = triggerEl.getBoundingClientRect()
    const spaceBelow = window.innerHeight - rect.bottom
    const openAbove = spaceBelow < menuHeight + 8 && rect.top > menuHeight + 8

    optionsEl.style.position = "fixed"
    optionsEl.style.width = `${menuWidth}px`
    optionsEl.style.left = `${Math.max(8, rect.right - menuWidth)}px`
    optionsEl.style.top = openAbove ? `${rect.top - menuHeight}px` : `${rect.bottom}px`
    optionsEl.style.zIndex = "1000000"

    this.openCellDropdown = { dropdownEl, optionsEl }

    this.boundCloseCellDropdown = (e) => {
      if (!optionsEl.contains(e.target) && !triggerEl.contains(e.target)) {
        this.#closeCellDropdown()
      }
    }
    this.boundRepositionOrCloseCellDropdown = () => this.#closeCellDropdown()

    // Defer so the opening click does not immediately close the menu.
    requestAnimationFrame(() => {
      document.addEventListener("click", this.boundCloseCellDropdown)
      window.addEventListener("scroll", this.boundRepositionOrCloseCellDropdown, true)
      window.addEventListener("resize", this.boundRepositionOrCloseCellDropdown)
    })
  }

  #closeCellDropdown() {
    if (!this.openCellDropdown) return

    const { dropdownEl, optionsEl } = this.openCellDropdown
    optionsEl.classList.add("hidden")
    optionsEl.style.position = ""
    optionsEl.style.width = ""
    optionsEl.style.left = ""
    optionsEl.style.top = ""
    optionsEl.style.zIndex = ""
    if (dropdownEl && dropdownEl.isConnected) {
      dropdownEl.appendChild(optionsEl)
    } else {
      optionsEl.remove()
    }

    document.removeEventListener("click", this.boundCloseCellDropdown)
    window.removeEventListener("scroll", this.boundRepositionOrCloseCellDropdown, true)
    window.removeEventListener("resize", this.boundRepositionOrCloseCellDropdown)
    this.openCellDropdown = null
    this.boundCloseCellDropdown = null
    this.boundRepositionOrCloseCellDropdown = null
  }

  #memberTypeToString(type) {
    switch (type) {
      case "admin": return "Admin"
      case "occupant": return "Occupant"
      case "owner": return "Owner"
      default: return "Unknown"
    }
  }

  get #csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.content || ""
  }
}
